import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Kontakt {
  id: string;
  kunde_id: string;
  name: string;
  name2?: string;
  adresse?: string;
  adresse2?: string;
  adresse3?: string;
  unternehmensname?: string;
  uebergeordneter_name?: string;
  uebergeordneter_kunde_id?: string;
  ort?: string;
  telefon?: string;
  mobile?: string;
  email?: string;
  letzte_aktivitaet?: string;
  bearbeitungsstatus_id?: number;
  bericht_erhalten?: number;
  status_id?: number;
  kontaktgruppe?: string;
  verbaende?: string;
  laender_regionscode?: string;
  plz?: string;
  sprache?: string;
  geburtsdatum?: string;
  avatar_bild?: string;
  activities?: string;
  account_id?: number;
  land?: string; // New field
  
  // Relationships (loaded via with())
  kunde?: any;
  uebergeordneterKunde?: any;
  bearbeitungsstatus?: any;
  status?: any;
  account?: {
    id: number;
    username: string;
    name: string;
    email: string;
    status: number;
    created_at: string;
    updated_at: string;
  };
}

export interface KontaktResponse {
  result: number;
  data: Kontakt[];
  total?: number;
  page?: number;
  limit?: number;
  sec: string;
}

export interface KontaktSingleResponse {
  result: number;
  data: Kontakt;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class KontaktService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { 
    console.log('KontaktService: Constructor called');
    console.log('KontaktService: apiUrl set to:', this.apiUrl);
  }

  /**
   * Get all kontakte with pagination
   */
  getKontakte(page: number = 1, limit: number = 10, search: string = '', sortBy: string = 'name', sortOrder: string = 'asc', kundeId?: string): Observable<KontaktResponse> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
      search: search,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    if (kundeId) {
      params.kunde_id = kundeId;
    }
    
    console.log('KontaktService: Making API call to', `${this.apiUrl}/kontakt`, 'with params:', params);
    return this.http.get<KontaktResponse>(`${this.apiUrl}/kontakt`, { params });
  }

  /**
   * Get single kontakt by ID
   */
  getKontakt(id: string): Observable<KontaktSingleResponse> {
    console.log('🔍 KontaktService: Making API call to get kontakt with ID:', id);
    console.log('🔍 KontaktService: Full URL:', `${this.apiUrl}/kontakt/${id}`);
    return this.http.get<KontaktSingleResponse>(`${this.apiUrl}/kontakt/${id}`);
  }

  /**
   * Create new kontakt
   */
  createKontakt(kontakt: Partial<Kontakt>): Observable<KontaktSingleResponse> {
    return this.http.post<KontaktSingleResponse>(`${this.apiUrl}/kontakt`, kontakt);
  }

  /**
   * Update kontakt
   */
  updateKontakt(id: string, kontakt: Partial<Kontakt>): Observable<KontaktSingleResponse> {
    return this.http.put<KontaktSingleResponse>(`${this.apiUrl}/kontakt/${id}`, kontakt);
  }

  /**
   * Delete kontakt
   */
  deleteKontakt(id: string): Observable<KontaktSingleResponse> {
    return this.http.delete<KontaktSingleResponse>(`${this.apiUrl}/kontakt/${id}`);
  }

  /**
   * Get kontakte by kunde ID
   */
  getKontakteByKundeId(kundeId: string, page: number = 1, limit: number = 10): Observable<KontaktResponse> {
    console.log('KontaktService: getKontakteByKundeId called with kundeId:', kundeId, 'page:', page, 'limit:', limit);
    return this.getKontakte(page, limit, '', 'name', 'asc', kundeId);
  }

  // ===========================================
  // ACCOUNT MANAGEMENT METHODS
  // ===========================================

  /**
   * Get account status for kontakt
   */
  getAccountStatus(kontaktId: string): Observable<any> {
    console.log('KontaktService: getAccountStatus called for kontaktId:', kontaktId);
    return this.http.get(`${this.apiUrl}/kontakt/${kontaktId}/account/status`);
  }

  /**
   * Create account for kontakt
   */
  createAccount(kontaktId: string): Observable<any> {
    console.log('KontaktService: createAccount called for kontaktId:', kontaktId);
    return this.http.post(`${this.apiUrl}/kontakt/${kontaktId}/account/create`, {});
  }

  /**
   * Recreate account for kontakt
   */
  recreateAccount(kontaktId: string): Observable<any> {
    console.log('KontaktService: recreateAccount called for kontaktId:', kontaktId);
    return this.http.post(`${this.apiUrl}/kontakt/${kontaktId}/account/recreate`, {});
  }

  /**
   * Remove account from kontakt
   */
  removeAccount(kontaktId: string): Observable<any> {
    console.log('KontaktService: removeAccount called for kontaktId:', kontaktId);
    return this.http.delete(`${this.apiUrl}/kontakt/${kontaktId}/account/remove`);
  }

  /**
   * Send account details via email
   */
  sendAccountDetails(kontaktId: string): Observable<any> {
    console.log('KontaktService: sendAccountDetails called for kontaktId:', kontaktId);
    return this.http.post(`${this.apiUrl}/kontakt/${kontaktId}/account/send-details`, {});
  }
}
